import React, { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { buildLeadStatus, nextLeadActions, relativeLabel } from "./leadStatusSnapshot";
import "./LeadStatusDrawer.css";

const RING_R = 36;
const RING_C = 2 * Math.PI * RING_R;

const dateLabel = (ms) => {
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const statusClass = (status) => String(status || "not contacted").toLowerCase().replace(/\s+/g, "-");

/**
 * Slide-in lead status. Sits under the fixed page header so Targets / Inbox /
 * Leads stay put, and covers the table from the right.
 */
const LeadStatusDrawer = ({ contact, flows, onClose, onOpenNotes, onAction }) => {
  const snap = useMemo(
    () => (contact ? buildLeadStatus(contact, { flows, now: Date.now() }) : null),
    [contact, flows]
  );

  useEffect(() => {
    if (!contact) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    // The scrim is visual only, so another lead-status badge stays clickable.
    // Any other click outside the panel closes it.
    const onDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".lsd-panel") || target.closest(".lead-status-trigger") || target.closest(".notes-modal-overlay") || target.closest(".popupOverlayWrapper")) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [contact, onClose]);

  if (!contact || !snap) return null;

  const offset = RING_C * (1 - snap.percent / 100);
  const subtitle = [snap.person, snap.email].filter(Boolean).join(" · ");
  const lastTouch = relativeLabel(snap.lastContactAt);
  const left = snap.totalSteps - snap.doneCount;
  const rank = questRank(snap.percent);
  const actions = onAction ? nextLeadActions(snap) : [];

  return createPortal(
    <div className="lsd-backdrop">
      <aside
        key={contact.id}
        className="lsd-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lsd-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="lsd-head">
          <div className="lsd-head-copy">
            <p className="lsd-kicker">Lead status</p>
            <h2 id="lsd-title" className="lsd-title">{snap.company}</h2>
            {subtitle && <p className="lsd-sub">{subtitle}</p>}
            <div className="lsd-pills">
              <span
                className={`contact-status-badge status-${statusClass(snap.badge.label)}`}
                style={snap.badge.color ? { backgroundColor: snap.badge.color, color: "#fff" } : undefined}
              >
                {snap.badge.label}
              </span>
              {snap.badge.isTag && (
                <span className={`contact-status-badge status-${statusClass(snap.status)}`}>{snap.status}</span>
              )}
            </div>
          </div>
          <button type="button" className="lsd-close" onClick={onClose} aria-label="Close lead status">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="lsd-body">
          <section className={`lsd-score${left === 0 ? " is-complete" : ""}`}>
            <div className={`lsd-ring${snap.percent > 0 ? " is-on" : ""}`} aria-label={`${snap.percent} percent of outreach steps logged`}>
              <svg viewBox="0 0 88 88">
                <defs>
                  <linearGradient id="lsd-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#146c3a" />
                    <stop offset="100%" stopColor="#2eae68" />
                  </linearGradient>
                </defs>
                <circle className="lsd-ring-track" cx="44" cy="44" r={RING_R} />
                <circle
                  className="lsd-ring-value"
                  cx="44"
                  cy="44"
                  r={RING_R}
                  style={{ strokeDasharray: RING_C, strokeDashoffset: offset }}
                />
              </svg>
              <div className="lsd-ring-label">
                <strong>{snap.percent}%</strong>
                <span>logged</span>
              </div>
            </div>
            <div className="lsd-score-copy">
              <p className="lsd-score-title">
                {snap.doneCount} of {snap.totalSteps}
                <span className="lsd-rank">{rank}</span>
              </p>
              <p className="lsd-score-hint">
                {left === 0
                  ? "Every step is logged"
                  : `${left} to go${lastTouch ? ` · last touch ${lastTouch.toLowerCase()}` : ""}`}
              </p>
              <div className="lsd-segments" aria-hidden="true">
                {snap.steps.map((step) => (
                  <span key={step.id} className={step.done ? "is-on" : ""} title={step.label} />
                ))}
              </div>
            </div>
          </section>

          {(snap.prepared || snap.openTasks > 0) && (
            <div className="lsd-now">
              {snap.prepared && <span>Prepared email waiting</span>}
              {snap.openTasks > 0 && <span>{snap.openTasks} open {snap.openTasks === 1 ? "task" : "tasks"}</span>}
            </div>
          )}

          <section className="lsd-block lsd-goals">
            <div className="lsd-block-head">
              <h3>Goals</h3>
              <span className="lsd-left">{left === 0 ? "All done" : `${left} to go`}</span>
            </div>
            <ul className="lsd-steps">
              {snap.steps.map((step) => (
                <li key={step.id} className={step.done ? "is-done" : "is-open"}>
                  <span className="lsd-step-icon"><i className={iconClass(step.icon)} /></span>
                  <span className="lsd-step-copy">
                    <span className="lsd-step-label">{step.label}</span>
                    <span className="lsd-step-detail">{step.detail}</span>
                  </span>
                  <span className="lsd-step-mark" aria-hidden="true">
                    {step.done ? <StepMark /> : null}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="lsd-facts">
            <Fact label="Emails" value={snap.emailsSent ? String(snap.emailsSent) : "—"} hint={emailHint(snap)} />
            <Fact label="Calls" value={snap.calls ? String(snap.calls) : "—"} hint={snap.calls ? "From notes" : (snap.phone || "No call note")} />
            <Fact label="Meetings" value={snap.meetings ? String(snap.meetings) : "—"} hint={snap.meetings ? "Logged" : "None yet"} />
            <Fact
              label="LinkedIn"
              value={linkedinValue(snap)}
              hint={linkedinHint(snap)}
              featured={snap.linkedin.done}
            />
            <Fact label="First contact" value={dateLabel(snap.firstContactAt)} hint={relativeLabel(snap.firstContactAt) || "Not yet"} />
            <Fact label="Last contact" value={dateLabel(snap.lastContactAt)} hint={lastTouch || "Not yet"} />
          </section>

          {(snap.linkedin.logs.length > 0 || snap.linkedin.inactive) && (
            <section className="lsd-block lsd-li">
              <h3>LinkedIn log</h3>
              {snap.linkedin.inactive && <p className="lsd-li-note">Marked inactive</p>}
              {snap.linkedin.logs.length > 0 && (
                <ol className="lsd-timeline">
                  {snap.linkedin.logs.map((log) => (
                    <li key={log.id} className="lsd-event lsd-event--linkedin">
                      <span className="lsd-event-icon"><i className="fa-brands fa-linkedin-in" /></span>
                      <span className="lsd-event-copy">
                        <span className="lsd-event-title">{log.stepLabel}</span>
                        {(log.name || log.role) && (
                          <span className="lsd-event-detail">{[log.name, log.role].filter(Boolean).join(" · ")}</span>
                        )}
                      </span>
                      {log.at ? <time dateTime={new Date(log.at).toISOString()}>{dateLabel(log.at)}</time> : null}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}

          <section className="lsd-block">
            <h3>What happened</h3>
            {snap.timeline.length === 0 && snap.logged.length === 0 ? (
              <p className="lsd-empty">Nothing logged yet. Emails, calls, LinkedIn and meetings show up here once they are saved on this contact.</p>
            ) : (
              <ol className="lsd-timeline">
                {snap.timeline.map((item) => (
                  <li key={item.id} className={`lsd-event lsd-event--${item.kind}`}>
                    <span className="lsd-event-icon"><i className={iconClass(item.icon)} /></span>
                    <span className="lsd-event-copy">
                      <span className="lsd-event-title">{item.title}</span>
                      {item.detail && <span className="lsd-event-detail">{item.detail}</span>}
                    </span>
                    <time dateTime={new Date(item.at).toISOString()}>{dateLabel(item.at)}</time>
                  </li>
                ))}
              </ol>
            )}
            {snap.logged.length > 0 && (
              <ul className="lsd-logged">
                {snap.logged.map((item) => (
                  <li key={item.id}>
                    <i className={iconClass(item.icon)} />
                    <span>{item.title}{item.detail ? ` — ${item.detail}` : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {snap.people.length > 0 && (
            <section className="lsd-block">
              <h3>People</h3>
              <ul className="lsd-people">
                {snap.people.map((person) => (
                  <li key={`${person.name}-${person.email}`}>
                    <span className="lsd-person-name">{person.name}</span>
                    <span className="lsd-person-role">{person.role || person.email}</span>
                    <span className={person.contacted ? "lsd-person-state is-on" : "lsd-person-state"}>
                      {person.contacted ? "Contacted" : "Not contacted"}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {snap.touchpoints.length > 0 && (
            <section className="lsd-block">
              <h3>Touchpoints</h3>
              <div className="lsd-touch">
                {snap.touchpoints.map((entry) => (
                  <span key={entry.key} className="lsd-touch-pill">
                    {entry.label}{entry.count > 1 ? ` · ${entry.count}` : ""}
                  </span>
                ))}
              </div>
            </section>
          )}

          {(snap.recentNotes.length > 0 || onOpenNotes) && (
            <section className="lsd-block">
              <div className="lsd-block-head">
                <h3>Notes{snap.notesCount ? ` · ${snap.notesCount}` : ""}</h3>
                {onOpenNotes && (
                  <button type="button" className="lsd-text-btn" onClick={() => onOpenNotes(contact)}>
                    Open notes
                  </button>
                )}
              </div>
              {snap.recentNotes.length === 0 ? (
                <p className="lsd-empty">No notes yet. A call is logged by saving a call note.</p>
              ) : (
                <ul className="lsd-notes">
                  {snap.recentNotes.map((note) => (
                    <li key={note.id || note.at || note.text}>
                      <span className="lsd-note-kicker">{note.title}{note.outcome ? ` · ${note.outcome}` : ""}</span>
                      {note.text && <p>{note.text}</p>}
                      {note.at && <time>{dateLabel(note.at)}</time>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {snap.flow && (
            <section className="lsd-block">
              <h3>Flow · {snap.flow.name}</h3>
              <p className="lsd-flow-meta">{snap.flow.doneCount} of {snap.flow.total} steps done</p>
              <ol className="lsd-flow">
                {snap.flow.steps.map((step, idx) => (
                  <li key={`${step.label}-${idx}`} className={step.done ? "is-done" : step.current ? "is-current" : ""}>
                    <span className="lsd-flow-index">{idx + 1}</span>
                    <span>{step.label}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>
        {actions.length > 0 && (
          <footer className="lsd-actions">
            {actions.map((action, index) => (
              <button
                key={action.id}
                type="button"
                className={index === 0 ? "altBtn altBtn--save" : "altBtn altBtn--petrol"}
                onClick={() => onAction(action.id)}
              >
                <i className={action.icon} aria-hidden="true" />
                {action.label}
              </button>
            ))}
          </footer>
        )}
      </aside>
    </div>,
    document.body
  );
};

const iconClass = (icon) => (icon.includes(" ") ? icon : `fa ${icon}`);

const emailHint = (snap) => {
  if (snap.emailsInferred) return "From status";
  const when = relativeLabel(snap.lastEmailAt);
  return when ? `Last ${when.toLowerCase()}` : "Sent";
};

const linkedinValue = (snap) => {
  const { connects, messages } = snap.linkedin;
  if (connects) return String(connects);
  if (messages) return String(messages);
  return "—";
};

const linkedinHint = (snap) => {
  const li = snap.linkedin;
  if (!li.done) return li.inactive ? "Inactive" : "No connect yet";
  const when = relativeLabel(li.lastAt);
  if (li.lastName) return [li.lastName, when].filter(Boolean).join(" · ");
  if (li.connects && li.messages) return `${li.messages} message${li.messages === 1 ? "" : "s"}`;
  if (li.messages && !li.connects) return li.messages === 1 ? "Message" : "Messages";
  return when || (li.connects === 1 ? "Connect" : "Connects");
};

const questRank = (percent) => {
  if (percent >= 100) return "Complete";
  if (percent >= 70) return "Almost there";
  if (percent >= 40) return "In motion";
  if (percent > 0) return "Started";
  return "Not started";
};

const StepMark = () => (
  <svg viewBox="0 0 12 10" width="12" height="10" aria-hidden="true">
    <path d="M1 5.2L4.2 8.4L11 1.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Fact = ({ label, value, hint, featured = false }) => (
  <div className={featured ? "lsd-fact is-on" : "lsd-fact"}>
    <span className="lsd-fact-label">{label}</span>
    <strong className="lsd-fact-value">{value}</strong>
    <span className="lsd-fact-hint">{hint}</span>
  </div>
);

export default LeadStatusDrawer;
